// Step 2 of writing a recipe (wireframe 19): type an ingredient the way
// you'd write it and the app splits it into amount, unit, name and prep and
// picks a grocery aisle (recipeEntry.ts, using the web importer's parser).
// Tap a row to correct it. Sections, notes, moving rows, and "Paste a whole
// list".
import { useState } from "react";
import { CATEGORIES, CATEGORY_LABELS, COLORS, FONT_SIZE, LAYOUT, NON_NUMERIC_UNITS, RADIUS, SPACE, UNITS } from "../../lib/constants";
import { buildGroupedRows } from "../../lib/recipeUtils";
import { prettyFractions } from "../../lib/recipeDisplay";
import { ingredientsFromText, type EntryIngredient } from "../../lib/recipeEntry";
import { uid } from "../../lib/utils";
import { smallLabel, supporting } from "../recipeStyles";
import { textButton } from "../plan/planStyles";
import { Field } from "./WriteParts";
import { fieldInput, primaryButton, secondaryButton, sectionsIn, stepHeading } from "./writeStyles";
import { RowMover, SectionHeader } from "./StepsStep";

type Row = { kind: "header"; key: string; label: string; itemId: string } | { kind: "item"; key: string; item: EntryIngredient; firstOfRun: boolean };

export function IngredientsStep({ items, unclear, onChange }: { items: EntryIngredient[]; unclear: string[]; onChange: (items: EntryIngredient[], unclear: string[]) => void }) {
  const [line, setLine] = useState("");
  const [section, setSection] = useState(() => items[items.length - 1]?.component ?? "");
  const [editing, setEditing] = useState<string | null>(null);
  const [pasting, setPasting] = useState(false);
  const [pasted, setPasted] = useState("");
  const [naming, setNaming] = useState(false);
  const [newSection, setNewSection] = useState("");
  // A section just started has no ingredients yet, but is still offered.
  const sections = [...new Set([...sectionsIn(items), ...(section ? [section] : [])])];

  const add = (text: string) => {
    const { ingredients, unclear: newUnclear } = ingredientsFromText(text, section);
    if (ingredients.length === 0) return;
    onChange([...items, ...ingredients], [...unclear, ...newUnclear]);
    setSection(ingredients[ingredients.length - 1].component);
  };
  const update = (id: string, patch: Partial<EntryIngredient>) =>
    onChange(
      items.map((i) => (i.id === id ? { ...i, ...patch } : i)),
      // Correcting an amount clears its "couldn't be read".
      "quantity" in patch || "unit" in patch ? unclear.filter((u) => u !== id) : unclear
    );
  const remove = (id: string) => {
    onChange(
      items.filter((i) => i.id !== id),
      unclear.filter((u) => u !== id)
    );
    setEditing(null);
  };
  const addNote = () => {
    const note: EntryIngredient = { id: uid(), component: section, type: "note", name: "", prep: "", quantity: "", unit: "", category: "other" };
    onChange([...items, note], unclear);
    setEditing(note.id);
  };
  const renameSection = (from: string, to: string) => {
    onChange(
      items.map((i) => (i.component === from ? { ...i, component: to } : i)),
      unclear
    );
    if (section === from) setSection(to);
  };

  const rows = buildGroupedRows(items) as Row[];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: SPACE.xl }}>
      <h1 style={stepHeading}>Ingredients</h1>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          add(line);
          setLine("");
        }}
        style={{ display: "flex", flexDirection: "column", gap: SPACE.xs }}
      >
        <label htmlFor="ingredient-line" style={{ ...smallLabel, color: COLORS.soft }}>
          Type an ingredient the way you&apos;d write it
        </label>
        <div style={{ display: "flex", gap: SPACE.md }}>
          <input id="ingredient-line" autoFocus value={line} placeholder="1.5 lb chicken thighs" onChange={(e) => setLine(e.target.value)} style={{ ...fieldInput, flex: 1 }} />
          <button type="submit" disabled={!line.trim()} style={{ ...primaryButton, opacity: line.trim() ? 1 : 0.6 }}>
            Add
          </button>
        </div>
        <span style={supporting}>
          The app splits each line into amount, unit, name and prep, and picks a grocery aisle. Tap a row to correct it.
          {sections.length > 0 && (
            <>
              {" "}
              Adding to:{" "}
              <select aria-label="Section new ingredients go into" value={section} onChange={(e) => setSection(e.target.value)} style={{ ...fieldInput, width: "auto", minHeight: 32, display: "inline-block" }}>
                <option value="">No section</option>
                {sections.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </>
          )}
        </span>
      </form>

      <div>
        {rows.map((row) =>
          row.kind === "header" ? (
            <SectionHeader key={row.key} label={row.label} onRename={(to) => renameSection(row.label, to)} />
          ) : (
            <IngredientRow
              key={row.key}
              item={row.item}
              items={items}
              unclear={unclear.includes(row.item.id)}
              editing={editing === row.item.id}
              onEdit={() => setEditing(editing === row.item.id ? null : row.item.id)}
              onUpdate={(patch) => update(row.item.id, patch)}
              onRemove={() => remove(row.item.id)}
              onMove={(next) => onChange(next as EntryIngredient[], unclear)}
            />
          )
        )}
        {items.length === 0 && <p style={{ ...supporting, margin: 0 }}>No ingredients yet.</p>}
      </div>

      {naming ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (newSection.trim()) setSection(newSection.trim());
            setNewSection("");
            setNaming(false);
          }}
          style={{ display: "flex", gap: SPACE.md, alignItems: "flex-end" }}
        >
          <Field label="New section name">
            <input autoFocus value={newSection} placeholder="For the sauce" onChange={(e) => setNewSection(e.target.value)} style={fieldInput} />
          </Field>
          <button type="submit" style={secondaryButton}>
            Start section
          </button>
        </form>
      ) : (
        <div style={{ display: "flex", flexWrap: "wrap", gap: SPACE.xl }}>
          <button type="button" onClick={() => setNaming(true)} style={textButton}>
            + Section
          </button>
          <button type="button" onClick={addNote} style={textButton}>
            + Note
          </button>
          <button type="button" aria-expanded={pasting} onClick={() => setPasting((p) => !p)} style={textButton}>
            Paste a whole list
          </button>
        </div>
      )}
      {naming === false && section && <span style={supporting}>New ingredients go into &ldquo;{section}&rdquo;.</span>}

      {pasting && (
        <div style={{ display: "flex", flexDirection: "column", gap: SPACE.md }}>
          <Field label="One ingredient per line" hint='A line like "For the sauce:" starts a section; "Note: ..." adds a note.'>
            <textarea autoFocus rows={8} value={pasted} onChange={(e) => setPasted(e.target.value)} style={{ ...fieldInput, padding: "var(--space-md) var(--space-lg)", fontFamily: "var(--font-sans)" }} />
          </Field>
          <button
            type="button"
            disabled={!pasted.trim()}
            onClick={() => {
              add(pasted);
              setPasted("");
              setPasting(false);
            }}
            style={{ ...primaryButton, alignSelf: "flex-start", opacity: pasted.trim() ? 1 : 0.6 }}
          >
            Add these
          </button>
        </div>
      )}
    </div>
  );
}

const amountText = (i: EntryIngredient) => [prettyFractions(i.quantity || ""), i.unit].filter(Boolean).join(" ");

function IngredientRow({
  item,
  items,
  unclear,
  editing,
  onEdit,
  onUpdate,
  onRemove,
  onMove,
}: {
  item: EntryIngredient;
  items: EntryIngredient[];
  unclear: boolean;
  editing: boolean;
  onEdit: () => void;
  onUpdate: (patch: Partial<EntryIngredient>) => void;
  onRemove: () => void;
  onMove: (items: { id: string; component: string }[]) => void;
}) {
  const isNote = item.type === "note";
  return (
    <div style={{ borderBottom: `1px solid ${COLORS.line}`, paddingLeft: item.component ? "var(--space-xl)" : 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: SPACE.sm }}>
        <button type="button" aria-expanded={editing} onClick={onEdit} style={{ flex: 1, minWidth: 0, minHeight: LAYOUT.tapTarget + 8, display: "flex", alignItems: "baseline", gap: SPACE.lg, padding: "var(--space-sm) 0", border: "none", background: "transparent", color: COLORS.ink, textAlign: "left", cursor: "pointer", fontSize: FONT_SIZE.base }}>
          {isNote ? (
            <span style={{ fontStyle: "italic", color: COLORS.soft }}>Note: {item.name || "(empty)"}</span>
          ) : (
            <>
              <strong style={{ minWidth: 64, flexShrink: 0 }}>{amountText(item)}</strong>
              <span style={{ minWidth: 0 }}>
                {item.name}
                {item.prep ? `, ${item.prep}` : ""}
                <span style={{ ...supporting, display: "block" }}>
                  Aisle: {(CATEGORY_LABELS as Record<string, string>)[item.category] ?? item.category}
                  {unclear && <strong style={{ color: COLORS.caution }}> · Amount unclear: check this one</strong>}
                </span>
              </span>
            </>
          )}
        </button>
        <RowMover items={items} id={item.id} onMove={onMove} label={item.name || "note"} />
      </div>
      {editing &&
        (isNote ? (
          <div style={{ display: "flex", flexDirection: "column", gap: SPACE.md, padding: "0 0 var(--space-lg)" }}>
            <Field label="Note">
              <input autoFocus value={item.name} placeholder="Parsley works in place of the lemon zest." onChange={(e) => onUpdate({ name: e.target.value })} style={fieldInput} />
            </Field>
            <EditButtons onDone={onEdit} onRemove={onRemove} />
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: SPACE.md, padding: "0 0 var(--space-lg)", background: COLORS.card, borderRadius: RADIUS.md }}>
            <Field label="Amount">
              <input inputMode="decimal" value={item.quantity || ""} placeholder="1 1/2" onChange={(e) => onUpdate({ quantity: e.target.value })} style={fieldInput} />
            </Field>
            <Field label="Unit">
              <select value={item.unit || ""} onChange={(e) => onUpdate({ unit: e.target.value, ...(NON_NUMERIC_UNITS.includes(e.target.value) ? { quantity: "" } : {}) })} style={fieldInput}>
                {[...UNITS, ...NON_NUMERIC_UNITS].map((u) => (
                  <option key={u} value={u}>
                    {u || "(none)"}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Ingredient">
              <input value={item.name} onChange={(e) => onUpdate({ name: e.target.value })} style={fieldInput} />
            </Field>
            <Field label="Prep">
              <input value={item.prep || ""} placeholder="minced" onChange={(e) => onUpdate({ prep: e.target.value })} style={fieldInput} />
            </Field>
            <div style={{ gridColumn: "1 / -1" }}>
              <Field label="Grocery aisle">
                <select value={item.category} onChange={(e) => onUpdate({ category: e.target.value })} style={fieldInput}>
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {(CATEGORY_LABELS as Record<string, string>)[c]}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <EditButtons onDone={onEdit} onRemove={onRemove} />
            </div>
          </div>
        ))}
    </div>
  );
}

export function EditButtons({ onDone, onRemove }: { onDone: () => void; onRemove: () => void }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: SPACE.md }}>
      <button type="button" onClick={onRemove} style={{ ...textButton, color: COLORS.danger }}>
        Delete
      </button>
      <button type="button" onClick={onDone} style={secondaryButton}>
        Done
      </button>
    </div>
  );
}
